import React, { useState, useEffect, useContext, createContext } from "react";
import "./App.css";

// Import Firebase simplifié pour éviter les erreurs
import { 
  requestNotificationPermission, 
  setupNotificationListener, 
  logOpticLinkEvent, 
  OpticLinkEvents,
  uploadOpticianDocument 
} from './firebase-config-simple.js';

// Import Google Auth
import GoogleAuth from './GoogleAuth.js';
import axios from "axios";
import { 
  Dashboard,
  MessagingCenter, 
  ContractManagement,
  RetrocessionManagement,
  ModernStoreProfileForm, 
  ModernOpticianProfileForm,
  ModernMissionForm,
  ModernMissionList, 
  ModernMissionSearch,
  ChatExpert,
  ActivityLogs,
  VerificationDocuments,
  AdminVerifications
} from './components';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo OpticLink - Version finale avec votre image
const OpticLinkLogo = ({ size = 48, white = false }) => {
  return (
    <div 
      className="flex-shrink-0 rounded-lg overflow-hidden"
      style={{ 
        width: size, 
        height: size,
        backgroundImage: `url("https://customer-assets.emergentagent.com/job_site-en-francais-1/artifacts/c1o5d48g_opticlink%20logo.jpg")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        ...(white ? {
          filter: 'brightness(0) invert(1) contrast(1.2)',
          border: '1px solid rgba(255,255,255,0.3)'
        } : {
          border: '1px solid rgba(59,130,246,0.3)',
          filter: 'contrast(1.1) saturate(1.1)'
        })
      }}
    >
      {/* Image de fallback cachée qui force le chargement */}
      <img 
        src="https://customer-assets.emergentagent.com/job_site-en-francais-1/artifacts/c1o5d48g_opticlink%20logo.jpg"
        alt="OPTIC LINK Logo"
        style={{ 
          width: '100%', 
          height: '100%', 
          objectFit: 'cover',
          opacity: 0
        }}
        onLoad={() => console.log('Logo OPTIC LINK chargé')}
        onError={() => console.error('Erreur logo')}
      />
    </div>
  );
};

// Context pour l'authentification
const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [fcmToken, setFcmToken] = useState(null);
  const [notifications, setNotifications] = useState([]);
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const [googleProcessing, setGoogleProcessing] = useState(() => window.location.hash?.includes('session_id='));
  const [googleAuthError, setGoogleAuthError] = useState('');

  useEffect(() => {
    // Retour de Google Auth : échanger le session_id (fragment URL) contre un token
    const hash = window.location.hash;
    if (!hash || !hash.includes('session_id=')) return;
    const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');
    const googleUserType = localStorage.getItem('google_auth_user_type');
    localStorage.removeItem('google_auth_user_type');

    axios.post(`${API}/auth/google/session`, {
      session_id: sessionId,
      user_type: googleUserType || null
    })
      .then((response) => {
        login(response.data);
      })
      .catch((error) => {
        const detail = error.response?.data?.detail;
        setGoogleAuthError(typeof detail === 'string' ? detail : 'Erreur de connexion avec Google');
      })
      .finally(() => {
        window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
        setGoogleProcessing(false);
      });
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserInfo();
      fetchNotifications();
      // Initialiser Firebase après connexion
      initializeFirebaseForUser();
      
      // Polling pour les notifications temps réel
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [token]);

  const initializeFirebaseForUser = async () => {
    try {
      // Demander permission notifications
      const fcmToken = await requestNotificationPermission();
      if (fcmToken) {
        setFcmToken(fcmToken);
        
        // Enregistrer le token côté serveur
        await fetch(`${import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL}/api/firebase/register-token`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ fcm_token: fcmToken })
        });
        
        console.log('🔔 Firebase notifications activées');
      }
      
      // Configurer l'écoute des notifications
      setupNotificationListener((payload) => {
        console.log('🔔 Notification reçue dans l\'app:', payload);
        // Ici on peut déclencher des actions dans l'UI
      });
      
      // Logger l'événement de connexion
      logOpticLinkEvent(OpticLinkEvents.LOGIN, {
        login_time: new Date().toISOString()
      });
      
    } catch (error) {
      console.error('❌ Erreur initialisation Firebase:', error);
    }
  };

  const fetchUserInfo = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Erreur récupération utilisateur:', error);
      logout();
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Erreur notifications:', error);
    }
  };

  const login = (tokenData) => {
    localStorage.setItem('token', tokenData.access_token);
    setToken(tokenData.access_token);
    setUser({
      user_id: tokenData.user_id,
      user_type: tokenData.user_type
    });
    axios.defaults.headers.common['Authorization'] = `Bearer ${tokenData.access_token}`;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setNotifications([]);
    delete axios.defaults.headers.common['Authorization'];
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? {...n, read: true} : n)
      );
    } catch (error) {
      console.error('Erreur marquage notification:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      login, 
      logout, 
      notifications, 
      markNotificationRead,
      fetchNotifications,
      googleProcessing,
      googleAuthError
    }}>
      {children}
    </AuthContext.Provider>
  );
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans AuthProvider');
  }
  return context;
};

// Composant d'accueil moderne
const Home = () => {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
      {/* Header moderne */}
      <div className="bg-slate-900/90 backdrop-blur-sm border-b border-slate-700 text-white p-6">
        <div className="container mx-auto">
          <div className="flex items-center justify-center">
            <div 
              className="flex-shrink-0 rounded-lg overflow-hidden"
              style={{ 
                width: 64, 
                height: 64,
                backgroundImage: `url("https://customer-assets.emergentagent.com/job_site-en-francais-1/artifacts/c1o5d48g_opticlink%20logo.jpg")`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            />
            <div className="ml-4">
              <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                OPTIC LINK
              </h1>
              <p className="text-slate-300 text-sm">Plateforme professionnelle pour l'optique</p>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="container mx-auto px-4 py-8">
        {user ? (
          <ModernUserDashboard />
        ) : (
          <ModernAuthSection />
        )}
      </div>
    </div>
  );
};

// Section d'authentification moderne avec récupération de mot de passe
const ModernAuthSection = () => {
  const [showLogin, setShowLogin] = useState(true);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [userType, setUserType] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    user_type: ''
  });
  const [resetData, setResetData] = useState({
    email: '',
    token: '',
    newPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetStep, setResetStep] = useState('request'); // 'request', 'confirm'
  const [resetMessage, setResetMessage] = useState('');
  const [showCodeVerification, setShowCodeVerification] = useState(false);
  const [codeEmail, setCodeEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [codeMessage, setCodeMessage] = useState('');
  const { login, googleProcessing, googleAuthError } = useAuth();

  useEffect(() => {
    if (googleAuthError) setError(googleAuthError);
  }, [googleAuthError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!showLogin && !userType) {
      setError('Veuillez sélectionner votre profil');
      return;
    }

    setLoading(true);
    setError('');

    try {
      if (showLogin) {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        login(response.data);
      } else {
        const response = await axios.post(`${API}/auth/register`, {
          email: formData.email,
          password: formData.password,
          user_type: userType
        });
        // Inscription réussie : afficher l'écran de saisie du code reçu par email
        setCodeEmail(formData.email);
        setCodeMessage(response.data.message || 'Un code de vérification a été envoyé à votre adresse email.');
        setShowCodeVerification(true);
      }
    } catch (error) {
      // Gestion spéciale des erreurs de connexion avec récupération automatique
      if (error.response?.status === 423 && error.response?.data?.recovery_triggered) {
        setError('');
        setResetMessage('Trop de tentatives échouées. Un lien de récupération a été envoyé à votre email et SMS.');
        setShowPasswordReset(true);
        setResetStep('confirm');
        setResetData(prev => ({ ...prev, email: formData.email }));
      } else if (error.response?.status === 403 && error.response?.data?.detail?.email_verified === false) {
        // Email non vérifié : afficher l'écran de saisie du code
        setError('');
        setCodeEmail(error.response.data.detail.email || formData.email);
        setCodeMessage('Votre email n\'est pas encore vérifié. Saisissez le code reçu par email (ou demandez-en un nouveau).');
        setShowCodeVerification(true);
      } else if (error.response?.status === 401 && error.response?.data?.detail?.includes('Tentative')) {
        // Afficher le nombre de tentatives restantes
        const errorDetail = error.response.data.detail;
        setError(typeof errorDetail === 'object' ? errorDetail.message : errorDetail);
      } else {
        // Extraire le message si detail est un objet
        const errorDetail = error.response?.data?.detail;
        if (typeof errorDetail === 'object' && errorDetail !== null) {
          setError(errorDetail.message || JSON.stringify(errorDetail));
        } else {
          setError(errorDetail || 'Une erreur est survenue');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (resetStep === 'request') {
        // Demander la récupération
        const response = await axios.post(`${API}/auth/forgot-password`, {
          email: resetData.email,
          recovery_method: 'email'
        });

        if (response.status === 200) {
          setResetMessage(`Un lien de récupération a été envoyé à ${response.data.contact_info}`);
          setResetStep('confirm');
        }
      } else {
        // Confirmer la réinitialisation
        const response = await axios.post(`${API}/auth/reset-password`, {
          token: resetData.token,
          new_password: resetData.newPassword
        });

        if (response.status === 200) {
          setResetMessage('Mot de passe réinitialisé avec succès ! Vous pouvez maintenant vous connecter.');
          setShowPasswordReset(false);
          setShowLogin(true);
          setFormData(prev => ({ ...prev, password: '' }));
          setResetData({ email: '', token: '', newPassword: '' });
          setResetStep('request');
        }
      }
    } catch (error) {
      // Extraire le message si detail est un objet
      const errorDetail = error.response?.data?.detail;
      if (typeof errorDetail === 'object' && errorDetail !== null) {
        setError(errorDetail.message || 'Erreur de connexion au serveur');
      } else {
        setError(errorDetail || 'Erreur de connexion au serveur');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API}/auth/verify-code`, {
        email: codeEmail,
        code: verificationCode
      });
      // Connexion automatique + redirection vers l'onglet Vérification (pièces justificatives)
      localStorage.setItem('post_signup_verification', '1');
      login(response.data);
    } catch (error) {
      const detail = error.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Code invalide');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.post(`${API}/auth/resend-verification`, { email: codeEmail });
      setCodeMessage(response.data.message);
    } catch (error) {
      const detail = error.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Erreur lors de l\'envoi du code');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (googleProcessing) {
    return (
      <div className="max-w-md mx-auto">
        <div data-testid="google-auth-processing" className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-2xl p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-white font-medium">Connexion avec Google en cours...</p>
        </div>
      </div>
    );
  }

  if (showCodeVerification) {
    return (
      <div className="max-w-md mx-auto">
        <div data-testid="code-verification-screen" className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <OpticLinkLogo size={56} white={false} />
            <h2 className="text-2xl font-bold text-white mt-4">Confirmez votre email</h2>
            <p className="text-slate-300 mt-2">
              Un code à 6 chiffres a été envoyé à<br /><strong className="text-white">{codeEmail}</strong>
            </p>
          </div>

          <form onSubmit={handleVerifyCode} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-2">Code de vérification</label>
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                data-testid="verification-code-input"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                className="w-full px-4 py-4 bg-white/10 border border-white/30 rounded-lg text-white text-center text-3xl tracking-[0.5em] font-bold placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm"
                placeholder="••••••"
                required
              />
            </div>

            {error && (
              <div className="p-4 bg-red-500/20 border border-red-500/30 text-red-200 rounded-lg backdrop-blur-sm" data-testid="code-error">
                {error}
              </div>
            )}
            {codeMessage && !error && (
              <div className="p-4 bg-blue-500/20 border border-blue-500/30 text-blue-200 rounded-lg backdrop-blur-sm">
                {codeMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || verificationCode.length !== 6}
              data-testid="verify-code-btn"
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-medium py-3 px-4 rounded-lg transition-all duration-300 disabled:opacity-50 shadow-lg"
            >
              {loading ? 'Vérification...' : '✅ Confirmer mon email'}
            </button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <button
              onClick={handleResendCode}
              disabled={loading}
              data-testid="resend-code-btn"
              className="text-blue-300 hover:text-blue-200 transition-colors text-sm"
            >
              📨 Renvoyer un code
            </button>
            <div>
              <button
                onClick={() => {
                  setShowCodeVerification(false);
                  setVerificationCode('');
                  setError('');
                  setCodeMessage('');
                  setShowLogin(true);
                }}
                className="text-slate-400 hover:text-white transition-colors text-sm"
              >
                Retour à la connexion
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!showLogin && !userType) {
    return (
      <div className="max-w-lg mx-auto">
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <OpticLinkLogo size={64} white={false} />
            <h2 className="text-2xl font-bold text-white mt-4">
              Rejoignez OPTIC LINK
            </h2>
            <p className="text-slate-300 mt-2">Choisissez votre profil professionnel</p>
          </div>
          
          <div className="space-y-4">
            <button
              onClick={() => setUserType('magasin')}
              className="group w-full p-6 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              <div className="flex items-center justify-center space-x-4">
                <div className="bg-white/20 rounded-full p-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-lg">Magasin d'Optique</h3>
                  <p className="text-sm text-blue-100">Recrutez des talents qualifiés</p>
                </div>
              </div>
            </button>
            
            <button
              onClick={() => setUserType('opticien')}
              className="group w-full p-6 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              <div className="flex items-center justify-center space-x-4">
                <div className="bg-white/20 rounded-full p-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div className="text-left">
                  <h3 className="font-semibold text-lg">Opticien Professionnel</h3>
                  <p className="text-sm text-emerald-100">Trouvez votre prochaine mission</p>
                </div>
              </div>
            </button>
          </div>
          
          <button
            onClick={() => setShowLogin(true)}
            className="w-full mt-6 text-slate-300 hover:text-white text-center py-2"
          >
            Déjà inscrit ? Se connecter
          </button>
        </div>
      </div>
    );
  }

  // Formulaire de récupération de mot de passe
  if (showPasswordReset) {
    return (
      <div className="max-w-md mx-auto">
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <OpticLinkLogo size={56} white={false} />
            <h2 className="text-2xl font-bold text-white mt-4">
              {resetStep === 'request' ? 'Récupération de mot de passe' : 'Nouveau mot de passe'}
            </h2>
            <p className="text-slate-300 mt-2">
              {resetStep === 'request' 
                ? 'Entrez votre email pour recevoir un lien de récupération'
                : 'Entrez votre nouveau mot de passe'
              }
            </p>
          </div>
          
          <form onSubmit={handlePasswordReset} className="space-y-6">
            {resetStep === 'request' ? (
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-2">
                  Email professionnel
                </label>
                <input
                  type="email"
                  value={resetData.email}
                  onChange={(e) => setResetData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm"
                  placeholder="votre@email.com"
                  required
                />
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-2">
                    Code de récupération
                  </label>
                  <input
                    type="text"
                    value={resetData.token}
                    onChange={(e) => setResetData(prev => ({ ...prev, token: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm"
                    placeholder="Code reçu par email/SMS"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-200 mb-2">
                    Nouveau mot de passe
                  </label>
                  <input
                    type="password"
                    value={resetData.newPassword}
                    onChange={(e) => setResetData(prev => ({ ...prev, newPassword: e.target.value }))}
                    className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </>
            )}
            
            {error && (
              <div className="p-4 bg-red-500/20 border border-red-500/30 text-red-200 rounded-lg backdrop-blur-sm">
                {error}
              </div>
            )}

            {resetMessage && (
              <div className="p-4 bg-green-500/20 border border-green-500/30 text-green-200 rounded-lg backdrop-blur-sm">
                {resetMessage}
              </div>
            )}
            
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-medium py-3 px-4 rounded-lg transition-all duration-300 disabled:opacity-50 shadow-lg"
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Traitement...</span>
                </div>
              ) : (
                resetStep === 'request' ? 'Envoyer le lien' : 'Réinitialiser'
              )}
            </button>
          </form>
          
          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setShowPasswordReset(false);
                setResetStep('request');
                setResetMessage('');
                setError('');
              }}
              className="text-slate-300 hover:text-white transition-colors"
            >
              Retour à la connexion
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-2xl p-8">
        <div className="text-center mb-8">
          <OpticLinkLogo size={56} white={false} />
          <h2 className="text-2xl font-bold text-white mt-4">
            {showLogin ? 'Connexion' : 'Inscription'}
          </h2>
          {!showLogin && userType && (
            <div className="mt-2 px-3 py-1 bg-white/20 rounded-full inline-block">
              <span className="text-sm text-slate-200">
                {userType === 'magasin' ? 'Magasin d\'Optique' : 'Opticien'}
              </span>
            </div>
          )}
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Email professionnel
            </label>
            <input
              type="email"
              name="email"
              data-testid="login-email-input"
              value={formData.email}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm"
              placeholder="votre@email.com"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-200 mb-2">
              Mot de passe
            </label>
            <input
              type="password"
              name="password"
              data-testid="login-password-input"
              value={formData.password}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-white/10 border border-white/30 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent backdrop-blur-sm"
              placeholder="••••••••"
              required
            />
            {showLogin && (
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordReset(true);
                    setResetData(prev => ({ ...prev, email: formData.email }));
                  }}
                  className="text-sm text-slate-300 hover:text-white transition-colors"
                >
                  Mot de passe oublié ?
                </button>
              </div>
            )}
          </div>
          
          {error && (
            <div className="p-4 bg-red-500/20 border border-red-500/30 text-red-200 rounded-lg backdrop-blur-sm">
              {error}
            </div>
          )}
          
          {resetMessage && (
            <div className="p-4 bg-green-500/20 border border-green-500/30 text-green-200 rounded-lg backdrop-blur-sm">
              {resetMessage}
            </div>
          )}

          {/* Google Authentication */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/30"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-slate-900 text-slate-300">Ou</span>
              </div>
            </div>
            <div className="mt-6">
              <GoogleAuth
                mode={showLogin ? "login" : "signup"}
                userType={!showLogin ? userType : null}
              />
            </div>
          </div>
          
          <button
            type="submit"
            disabled={loading}
            data-testid="auth-submit-btn"
            className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-medium py-3 px-4 rounded-lg transition-all duration-300 disabled:opacity-50 shadow-lg"
          >
            {loading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Connexion...</span>
              </div>
            ) : (
              showLogin ? 'Se connecter' : 'Créer mon compte'
            )}
          </button>
        </form>
        
        <div className="mt-6 text-center">
          <button
            onClick={() => {
              setShowLogin(!showLogin);
              setUserType('');
              setError('');
            }}
            className="text-slate-300 hover:text-white transition-colors"
          >
            {showLogin ? 'Créer un compte' : 'Déjà inscrit ? Se connecter'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Dashboard utilisateur moderne
const ModernUserDashboard = () => {
  const { user, token, logout, notifications } = useAuth();
  const urlParams = new URLSearchParams(window.location.search);
  const postSignup = localStorage.getItem('post_signup_verification') === '1';
  if (postSignup) localStorage.removeItem('post_signup_verification');
  const initialTab = user.user_type === 'admin'
    ? 'admin-verifications'
    : postSignup
      ? 'verification'
      : ((urlParams.get('payment') === 'retrocession' || urlParams.get('payment') === 'cancelled') && user.user_type === 'magasin') ? 'retrocession' : 'dashboard';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showNotifications, setShowNotifications] = useState(false);

  const unreadCount = notifications.filter(n => !n.read).length;

  const tabs = user.user_type === 'admin' ? [
    { id: 'admin-verifications', label: 'Vérifications', icon: '🛡️' },
    { id: 'activity-logs', label: 'Journal', icon: '📋' }
  ] : user.user_type === 'magasin' ? [
    { id: 'dashboard', label: 'Tableau de bord', icon: '📊' },
    { id: 'verification', label: 'Vérification', icon: '🛡️' },
    { id: 'missions', label: 'Mes Missions', icon: '🎯' },
    { id: 'retrocession', label: 'Rétrocession', icon: '🔄' },
    { id: 'candidates', label: 'Candidatures', icon: '👥' },
    { id: 'contracts', label: 'Contrats', icon: '📄' },
    { id: 'chat-expert', label: 'Assistant Opticien IA', icon: '🤖' },
    { id: 'activity-logs', label: 'Journal', icon: '📋' },
    { id: 'profile', label: 'Profil', icon: '🏢' },
    { id: 'messages', label: 'Messages', icon: '💬' }
  ] : [
    { id: 'dashboard', label: 'Tableau de bord', icon: '📊' },
    { id: 'verification', label: 'Vérification', icon: '🛡️' },
    { id: 'search', label: 'Missions', icon: '🔍' },
    { id: 'applications', label: 'Candidatures', icon: '📋' },
    { id: 'contracts', label: 'Contrats', icon: '📄' },
    { id: 'chat-expert', label: 'Assistant Opticien IA', icon: '🤖' },
    { id: 'activity-logs', label: 'Journal', icon: '📋' },
    { id: 'profile', label: 'Profil', icon: '👤' },
    { id: 'messages', label: 'Messages', icon: '💬' }
  ];

  return (
    <div className="min-h-screen">
      {/* Header moderne du dashboard */}
      <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl mb-8 overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <OpticLinkLogo size={40} white={false} />
              <div>
                <h2 className="text-xl font-bold text-white">
                  {user.user_type === 'admin' ? 'Espace Administrateur' : user.user_type === 'magasin' ? 'Espace Recruteur' : 'Espace Professionnel'}
                </h2>
                <p className="text-slate-400 text-sm">
                  {user.user_type === 'admin' ? 'Gérez les vérifications et la plateforme' : user.user_type === 'magasin' ? 'Gérez vos missions et recrutements' : 'Trouvez votre prochaine opportunité'}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 text-slate-400 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM10.07 7.07a3 3 0 000-4.24l1.41-1.42a5 5 0 010 7.08l-1.41-1.42z" />
                  </svg>
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                
                {showNotifications && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-slate-800/90 backdrop-blur-md border border-slate-600 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
                    <div className="p-4 border-b border-slate-600">
                      <h3 className="font-semibold text-white">Notifications</h3>
                    </div>
                    <div className="p-2">
                      {notifications.length > 0 ? (
                        notifications.slice(0, 10).map(notification => (
                          <div key={notification.id} className={`p-3 rounded-lg mb-2 ${notification.read ? 'bg-slate-700/50' : 'bg-blue-600/20'}`}>
                            <h4 className="text-sm font-medium text-white">{notification.title}</h4>
                            <p className="text-xs text-slate-400 mt-1">{notification.message}</p>
                          </div>
                        ))
                      ) : (
                        <p className="text-slate-400 text-center py-4">Aucune notification</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <button
                onClick={logout}
                className="bg-red-600/20 hover:bg-red-600/30 text-red-400 hover:text-red-300 px-4 py-2 rounded-lg transition-all duration-200"
              >
                Déconnexion
              </button>
            </div>
          </div>
        </div>
        
        {/* Navigation tabs moderne */}
        <div className="px-6 py-2">
          <div className="flex space-x-1 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-blue-600/30 text-blue-300 border border-blue-500/30'
                    : 'text-slate-400 hover:text-white hover:bg-white/10'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
      
      {/* Contenu */}
      <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-xl p-6">
        {activeTab === 'dashboard' && <Dashboard />}
        {activeTab === 'verification' && <VerificationDocuments />}
        {activeTab === 'admin-verifications' && user.user_type === 'admin' && <AdminVerifications />}
        {activeTab === 'profile' && <ProfileContent />}
        {activeTab === 'missions' && user.user_type === 'magasin' && <MissionsContent />}
        {activeTab === 'retrocession' && user.user_type === 'magasin' && <RetrocessionManagement />}
        {activeTab === 'search' && user.user_type === 'opticien' && <SearchContent />}
        {activeTab === 'contracts' && <ContractManagement />}
        {activeTab === 'chat-expert' && <ChatExpert />}
        {activeTab === 'activity-logs' && <ActivityLogs token={token} />}
        {activeTab === 'messages' && <MessagingCenter />}
      </div>
    </div>
  );
};

// Placeholder pour les composants de contenu
const ProfileContent = () => {
  const { user } = useAuth();
  return (
    <div>
      {user.user_type === 'magasin' ? (
        <ModernStoreProfileForm />
      ) : (
        <ModernOpticianProfileForm />
      )}
    </div>
  );
};

const MissionsContent = () => <ModernMissionList />;
const SearchContent = () => <ModernMissionSearch />;

function App() {
  return (
    <AuthProvider>
      <div className="App min-h-screen">
        <Home />
      </div>
    </AuthProvider>
  );
}

export default App;